import React from "react"
import { Link, useParams } from "@tanstack/react-router"
import {
  useAdminOrganizations,
  useUpdateOrganization,
  useAdminUsers,
  useCreateUser,
  useUpdateUser,
  useDeleteUser,
  useSendPasswordReset,
  useUnlockUser,
  useAssignments,
  useUpdateAssignments,
  useWorkflowAssignments,
  useUpdateWorkflowAssignments,
  useCurrentAdmin,
  useAdminApiApplications,
  useCreateAdminApiApplication,
  useRotateAdminApiApplication,
  useRevokeAdminApiApplication,
  useCreateOrganization,
  useQuizAvailability,
  useUpdateQuizAvailability,
} from "@/lib/queries"
import type { ApiApplicationWithSecret, QuizAvailabilityRow } from "@/lib/types"
import { hasPermission, PERMISSIONS } from "@/lib/permissions"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { ArrowLeft, MoreHorizontal, Plus, Copy } from "lucide-react"
import { toast } from "sonner"
import {
  SsoChangeConfirmDialog,
  ssoChanges,
  type SsoChange,
} from "@/components/SsoChangeConfirmDialog"

export function OrganizationDetailPage() {
  const params = useParams({ strict: false }) as { id: string }
  const orgId = Number(params.id)

  const { data: organizations = [] } = useAdminOrganizations()
  const organization = organizations.find((o) => o.id === orgId)
  const parentOrg = organization?.parent_organization_id
    ? organizations.find((o) => o.id === organization.parent_organization_id)
    : undefined
  const subOrgs = organizations.filter((o) => o.parent_organization_id === orgId)
  const isSubOrg = Boolean(organization?.parent_organization_id)
  const showPharmacySites =
    !isSubOrg && ((organization?.max_sub_organizations ?? 0) > 0 || subOrgs.length > 0)
  const atCap = subOrgs.length >= (organization?.max_sub_organizations ?? 0)

  const updateOrganization = useUpdateOrganization()
  const { data: users = [] } = useAdminUsers(orgId)
  const createUser = useCreateUser()
  const updateUser = useUpdateUser()
  const deleteUser = useDeleteUser()
  const sendPasswordReset = useSendPasswordReset()
  const unlockUser = useUnlockUser()
  const { data: assignments = [] } = useAssignments(orgId)
  const updateAssignments = useUpdateAssignments()
  const { data: workflowAssignments = [] } = useWorkflowAssignments(orgId)
  const updateWorkflowAssignments = useUpdateWorkflowAssignments()
  const { data: quizAvailability = [] } = useQuizAvailability(orgId)
  const updateQuizAvailability = useUpdateQuizAvailability()
  const { data: currentAdmin } = useCurrentAdmin()
  const canManageQuizzes = hasPermission(currentAdmin, PERMISSIONS.MANAGE_QUIZZES)
  const isSuperAdmin = currentAdmin?.role === "super_admin"
  const {
    data: apiApplications = [],
    isLoading: apiApplicationsLoading,
    isError: apiApplicationsError,
  } = useAdminApiApplications(orgId)
  const createApiApplication = useCreateAdminApiApplication(orgId)
  const rotateApiApplication = useRotateAdminApiApplication(orgId)
  const revokeApiApplication = useRevokeAdminApiApplication(orgId)

  // Organization state
  const [orgName, setOrgName] = React.useState("")
  const [orgActive, setOrgActive] = React.useState(true)
  const [orgQuizzesEnabled, setOrgQuizzesEnabled] = React.useState(false)
  const [orgPreviewMode, setOrgPreviewMode] = React.useState(false)
  const [orgApiAccessEnabled, setOrgApiAccessEnabled] = React.useState(false)
  const [maxSubOrgs, setMaxSubOrgs] = React.useState(0)
  const [orgSsoDomain, setOrgSsoDomain] = React.useState("")
  const [orgEntraTenantId, setOrgEntraTenantId] = React.useState("")

  // SSO change confirmation dialog state
  const [ssoConfirmOpen, setSsoConfirmOpen] = React.useState(false)
  const [ssoPendingChanges, setSsoPendingChanges] = React.useState<SsoChange[]>([])

  // User dialog state
  const [userDialogOpen, setUserDialogOpen] = React.useState(false)
  const [newUsername, setNewUsername] = React.useState("")
  const [newEmail, setNewEmail] = React.useState("")

  // API application dialog state
  const [apiAppDialogOpen, setApiAppDialogOpen] = React.useState(false)
  const [newApiAppName, setNewApiAppName] = React.useState("")
  const [revealedApiApp, setRevealedApiApp] = React.useState<ApiApplicationWithSecret | null>(null)

  // Pharmacy site (sub-organization) dialog state
  const [pharmacySiteDialogOpen, setPharmacySiteDialogOpen] = React.useState(false)
  const [newPharmacySiteName, setNewPharmacySiteName] = React.useState("")
  const [newPharmacySiteSso, setNewPharmacySiteSso] = React.useState("")
  const [newPharmacySiteEntra, setNewPharmacySiteEntra] = React.useState("")
  const createOrganization = useCreateOrganization()

  // Assignments state
  const [assignmentChanges, setAssignmentChanges] = React.useState<Record<number, boolean>>({})
  const [workflowAssignmentChanges, setWorkflowAssignmentChanges] = React.useState<Record<string, boolean>>({})
  const [quizAvailabilityChanges, setQuizAvailabilityChanges] = React.useState<Record<number, boolean>>({})

  React.useEffect(() => {
    if (organization) {
      setOrgName(organization.name)
      setOrgActive(organization.active)
      setOrgQuizzesEnabled(organization.quizzes_enabled)
      setOrgPreviewMode(organization.preview_mode)
      setOrgApiAccessEnabled(organization.api_access_enabled)
      setMaxSubOrgs(organization.max_sub_organizations)
      setOrgSsoDomain(organization.sso_domain ?? "")
      setOrgEntraTenantId(organization.entra_tenant_id ?? "")
    }
  }, [organization])

  const doSaveOrganization = async () => {
    try {
      await updateOrganization.mutateAsync({
        id: orgId,
        name: orgName,
        active: orgActive,
        ...(isSubOrg ? {} : { max_sub_organizations: maxSubOrgs, quizzes_enabled: orgQuizzesEnabled, preview_mode: orgPreviewMode, api_access_enabled: orgApiAccessEnabled }),
        sso_domain: orgSsoDomain.trim() || null,
        entra_tenant_id: orgEntraTenantId.trim() || null,
      })
      toast.success("Organization updated successfully")
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to update organization")
    }
  }

  const handleSaveOrganization = () => {
    if (!organization) return
    const changes = ssoChanges(
      {
        sso_domain: organization.sso_domain,
        entra_tenant_id: organization.entra_tenant_id,
      },
      {
        sso_domain: orgSsoDomain.trim() || null,
        entra_tenant_id: orgEntraTenantId.trim() || null,
      }
    )
    if (changes.length === 0) {
      void doSaveOrganization()
      return
    }
    setSsoPendingChanges(changes)
    setSsoConfirmOpen(true)
  }

  const handleCreatePharmacySite = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await createOrganization.mutateAsync({
        name: newPharmacySiteName,
        parent_organization_id: orgId,
        ...(newPharmacySiteSso.trim() ? { sso_domain: newPharmacySiteSso.trim() } : {}),
        ...(newPharmacySiteEntra.trim() ? { entra_tenant_id: newPharmacySiteEntra.trim() } : {}),
      })
      setPharmacySiteDialogOpen(false)
      setNewPharmacySiteName("")
      setNewPharmacySiteSso("")
      setNewPharmacySiteEntra("")
      toast.success("Pharmacy site created")
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to create pharmacy site")
    }
  }

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newUsername.trim() || !newEmail.trim()) return

    try {
      await createUser.mutateAsync({
        orgId,
        username: newUsername.trim(),
        email: newEmail.trim(),
      })
      toast.success("User created. Welcome email sent with password setup instructions.")
      setUserDialogOpen(false)
      setNewUsername("")
      setNewEmail("")
    } catch (error) {
      toast.error("Failed to create user")
    }
  }

  const handleToggleUserActive = async (userId: number, currentActive: boolean) => {
    try {
      await updateUser.mutateAsync({
        id: userId,
        orgId,
        active: !currentActive,
      })
      toast.success(`User ${!currentActive ? "activated" : "deactivated"}`)
    } catch (error) {
      toast.error("Failed to update user")
    }
  }

  const handleToggleManager = async (userId: number, currentRole: "staff" | "manager") => {
    const nextRole = currentRole === "manager" ? "staff" : "manager"
    try {
      await updateUser.mutateAsync({
        id: userId,
        orgId,
        role: nextRole,
      })
      toast.success(
        nextRole === "manager" ? "User is now a manager" : "Manager role removed"
      )
    } catch (error) {
      toast.error("Failed to update user role")
    }
  }

  const handleSendPasswordReset = async (userId: number) => {
    try {
      await sendPasswordReset.mutateAsync(userId)
      toast.success("Password reset email sent")
    } catch (error) {
      toast.error("Failed to send password reset")
    }
  }

  const handleUnlockUser = async (userId: number) => {
    try {
      await unlockUser.mutateAsync({ id: userId, orgId })
      toast.success("Account unlocked")
    } catch (error) {
      toast.error("Failed to unlock account")
    }
  }

  const handleDeleteUser = async (userId: number, username: string) => {
    if (!confirm(`Are you sure you want to delete user "${username}"? This action cannot be undone.`)) {
      return
    }

    try {
      await deleteUser.mutateAsync({ id: userId, orgId })
      toast.success("User deleted successfully")
    } catch (error) {
      toast.error("Failed to delete user")
    }
  }

  const openCreateApiApplication = () => {
    setRevealedApiApp(null)
    setNewApiAppName("")
    setApiAppDialogOpen(true)
  }

  const closeApiApplicationDialog = () => {
    setApiAppDialogOpen(false)
    setRevealedApiApp(null)
    setNewApiAppName("")
  }

  const handleCreateApiApplication = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newApiAppName.trim()) return

    try {
      const app = await createApiApplication.mutateAsync({ name: newApiAppName.trim() })
      setRevealedApiApp(app)
      setNewApiAppName("")
      toast.success("Application created")
    } catch (error) {
      toast.error("Failed to create application")
    }
  }

  const handleRotateApiApplication = async (id: number) => {
    if (!confirm("Rotate this secret? The current secret stops working immediately.")) return

    try {
      const app = await rotateApiApplication.mutateAsync(id)
      setRevealedApiApp(app)
      setApiAppDialogOpen(true)
      toast.success("Secret rotated")
    } catch (error) {
      toast.error("Failed to rotate secret")
    }
  }

  const handleRevokeApiApplication = async (id: number) => {
    if (!confirm("Revoke this application? Its tokens stop working within the hour.")) return

    try {
      await revokeApiApplication.mutateAsync(id)
      toast.success("Application revoked")
    } catch (error) {
      toast.error("Failed to revoke application")
    }
  }

  const handleToggleAssignment = (documentId: number, currentActive: boolean) => {
    setAssignmentChanges((prev) => ({
      ...prev,
      [documentId]: !currentActive,
    }))
  }

  const handleSaveAssignments = async () => {
    const updates = Object.entries(assignmentChanges).map(([documentId, active]) => ({
      document_id: Number(documentId),
      active,
    }))

    if (updates.length === 0) {
      toast.info("No changes to save")
      return
    }

    try {
      await updateAssignments.mutateAsync({
        orgId,
        assignments: updates,
      })
      toast.success("Assignments updated successfully")
      setAssignmentChanges({})
    } catch (error) {
      toast.error("Failed to update assignments")
    }
  }

  const waKey = (wfId: number, crId: number) => `${wfId}:${crId}`

  const handleToggleWorkflowAssignment = (wfId: number, crId: number, currentActive: boolean) => {
    setWorkflowAssignmentChanges((prev) => ({
      ...prev,
      [waKey(wfId, crId)]: !currentActive,
    }))
  }

  const handleSaveWorkflowAssignments = async () => {
    if (Object.keys(workflowAssignmentChanges).length === 0) {
      toast.info("No changes to save")
      return
    }

    const updates = Object.entries(workflowAssignmentChanges).map(([key, active]) => {
      const [wfId, crId] = key.split(":").map(Number)
      return { workflow_document_id: wfId, clinical_review_document_id: crId, active }
    })

    try {
      await updateWorkflowAssignments.mutateAsync({
        orgId,
        workflow_assignments: updates,
      })
      toast.success("Workflow assignments updated successfully")
      setWorkflowAssignmentChanges({})

      // Auto-assign clinical reviews that were enabled via workflow pairs
      const enabledCrIds = new Set(
        updates.filter((u) => u.active).map((u) => u.clinical_review_document_id)
      )
      if (enabledCrIds.size > 0) {
        const crAssignments = assignments.filter(
          (a) => a.document_type === "clinical_review" && enabledCrIds.has(a.document_id)
        )
        const crUpdates = crAssignments
          .filter((a) => {
            const current = assignmentChanges[a.document_id] ?? a.active
            return !current // only enable CRs that aren't already active
          })
          .map((a) => ({ document_id: a.document_id, active: true }))

        if (crUpdates.length > 0) {
          try {
            await updateAssignments.mutateAsync({
              orgId,
              assignments: crUpdates,
            })
            const names = crAssignments
              .filter((a) => crUpdates.some((u) => u.document_id === a.document_id))
              .map((a) => a.document_title)
            toast.success(`Clinical reviews auto-assigned: ${names.join(", ")}`)
            // Clear any local changes for auto-assigned CRs
            setAssignmentChanges((prev) => {
              const next = { ...prev }
              crUpdates.forEach((u) => delete next[u.document_id])
              return next
            })
          } catch {
            toast.error("Workflow assignments saved, but failed to auto-assign clinical reviews")
          }
        }
      }
    } catch (error) {
      toast.error("Failed to update workflow assignments")
    }
  }

  // Whether a quiz is on for this organization, with unsaved edits applied
  const quizIsActive = (row: QuizAvailabilityRow) =>
    quizAvailabilityChanges[row.quiz_id] ?? row.active

  const handleToggleQuizAvailability = (row: QuizAvailabilityRow) => {
    setQuizAvailabilityChanges((prev) => ({
      ...prev,
      [row.quiz_id]: !quizIsActive(row),
    }))
  }

  const handleSaveQuizAvailability = async () => {
    const availabilities = Object.entries(quizAvailabilityChanges).map(
      ([quizId, active]) => ({ quiz_id: Number(quizId), active })
    )
    if (availabilities.length === 0) {
      toast.info("No changes to save")
      return
    }

    try {
      await updateQuizAvailability.mutateAsync({ orgId, availabilities })
      toast.success("Assessment availability updated successfully")
      setQuizAvailabilityChanges({})
    } catch (error) {
      toast.error("Failed to update assessment availability")
    }
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Never"
    return new Date(dateString).toLocaleDateString("en-CA")
  }

  // Group assignments by document type
  const assignmentsByType = React.useMemo(() => {
    const grouped: Record<string, typeof assignments> = {}
    assignments.forEach((assignment) => {
      if (assignment.document_type === "workflow_template") return
      if (!grouped[assignment.document_type]) {
        grouped[assignment.document_type] = []
      }
      grouped[assignment.document_type].push(assignment)
    })
    return grouped
  }, [assignments])

  // Group workflow assignments by workflow
  const workflowAssignmentsByWorkflow = React.useMemo(() => {
    const grouped: Record<string, typeof workflowAssignments> = {}
    workflowAssignments.forEach((wa) => {
      if (!grouped[wa.workflow_document_title]) {
        grouped[wa.workflow_document_title] = []
      }
      grouped[wa.workflow_document_title].push(wa)
    })
    return grouped
  }, [workflowAssignments])

  if (!organization) {
    return (
      <div className="space-y-6 p-6">
        <Button variant="outline" asChild>
          <Link to="/admin/users">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Users
          </Link>
        </Button>
        <p className="text-muted-foreground">Organization not found</p>
      </div>
    )
  }

  return (
    <div className="space-y-6 overflow-auto p-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" asChild>
          <Link to="/admin/users">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Organization Details
          </h1>
          <p className="text-sm text-muted-foreground">
            Manage settings, users, and document assignments
          </p>
        </div>
      </div>

      {isSubOrg && (
        <p className="text-sm text-muted-foreground">
          Pharmacy site of{" "}
          <Link
            to="/admin/organizations/$id"
            params={{ id: String(organization.parent_organization_id) }}
            className="font-medium hover:underline"
          >
            {parentOrg?.name ?? "parent organization"}
          </Link>
        </p>
      )}

      {/* Organization Header */}
      <Card>
        <CardHeader>
          <CardTitle>Organization Settings</CardTitle>
          <CardDescription>
            Update the organization name and status
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="orgName">Organization Name</Label>
              <Input
                id="orgName"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="orgActive">Status</Label>
              <div className="flex items-center gap-2">
                <Switch
                  id="orgActive"
                  checked={orgActive}
                  onCheckedChange={setOrgActive}
                />
                <span className="text-sm">
                  {orgActive ? "Active" : "Deactivated"}
                </span>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="orgQuizzesEnabled">Assessments</Label>
              {isSubOrg ? (
                <p className="text-sm text-muted-foreground">
                  {parentOrg?.quizzes_enabled ? "Enabled" : "Disabled"}, inherited from{" "}
                  {parentOrg?.name ?? "the parent organization"}
                </p>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <Switch
                      id="orgQuizzesEnabled"
                      checked={orgQuizzesEnabled}
                      onCheckedChange={setOrgQuizzesEnabled}
                    />
                    <span className="text-sm">
                      {orgQuizzesEnabled ? "Enabled" : "Disabled"}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Shows training and assessment features to this organization's users and its pharmacy sites.
                  </p>
                </>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="orgPreviewMode">Demo org (preview mode)</Label>
              {isSubOrg ? (
                <p className="text-sm text-muted-foreground">
                  {parentOrg?.preview_mode ? "Enabled" : "Disabled"}, inherited from{" "}
                  {parentOrg?.name ?? "the parent organization"}
                </p>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <Switch
                      id="orgPreviewMode"
                      checked={orgPreviewMode}
                      onCheckedChange={setOrgPreviewMode}
                    />
                    <span className="text-sm">
                      {orgPreviewMode ? "Enabled" : "Disabled"}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Users see a preview of each document: the first section, then headings only. PDF export is off.
                  </p>
                </>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="orgApiAccessEnabled">API access</Label>
              {isSubOrg ? (
                <p className="text-sm text-muted-foreground">
                  {parentOrg?.api_access_enabled ? "Enabled" : "Disabled"}, inherited from{" "}
                  {parentOrg?.name ?? "the parent organization"}
                </p>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <Switch
                      id="orgApiAccessEnabled"
                      checked={orgApiAccessEnabled}
                      onCheckedChange={setOrgApiAccessEnabled}
                    />
                    <span className="text-sm">
                      {orgApiAccessEnabled ? "Enabled" : "Disabled"}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Lets this organization create API applications and use the external documents API.
                  </p>
                </>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="orgSsoDomain">Google SSO domain</Label>
              <Input
                id="orgSsoDomain"
                value={orgSsoDomain}
                onChange={(e) => setOrgSsoDomain(e.target.value)}
                placeholder="example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="orgEntraTenantId">Microsoft Entra tenant ID</Label>
              <Input
                id="orgEntraTenantId"
                value={orgEntraTenantId}
                onChange={(e) => setOrgEntraTenantId(e.target.value)}
              />
            </div>
            {!isSubOrg && (
              <div className="space-y-2">
                <Label htmlFor="maxSubOrgs">Pharmacy site limit</Label>
                <Input
                  id="maxSubOrgs"
                  type="number"
                  min={0}
                  value={maxSubOrgs}
                  onChange={(e) => setMaxSubOrgs(Math.max(0, Number(e.target.value) || 0))}
                />
                <p className="text-xs text-muted-foreground">
                  Pharmacy sites this organization can manage ({organization.sub_organization_count} in use). 0 disables the feature.
                </p>
              </div>
            )}
          </div>
          <Button
            onClick={handleSaveOrganization}
            disabled={updateOrganization.isPending}
          >
            {updateOrganization.isPending ? "Saving..." : "Save Organization"}
          </Button>
        </CardContent>
      </Card>

      {showPharmacySites && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="space-y-1.5">
                <CardTitle>Pharmacy Sites</CardTitle>
                <CardDescription>
                  {subOrgs.length} of {organization.max_sub_organizations} pharmacy sites
                </CardDescription>
              </div>
              <Button onClick={() => setPharmacySiteDialogOpen(true)} disabled={atCap}>
                <Plus className="mr-2 h-4 w-4" />
                Add pharmacy site
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {subOrgs.length === 0 ? (
              <p className="text-sm text-muted-foreground">No pharmacy sites yet.</p>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Users</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {subOrgs.map((s) => (
                      <TableRow key={s.id}>
                        <TableCell>
                          <Link
                            to="/admin/organizations/$id"
                            params={{ id: String(s.id) }}
                            className="font-medium hover:underline"
                          >
                            {s.name}
                          </Link>
                        </TableCell>
                        <TableCell className="text-xs">
                          <span className={s.active ? "text-lime-600 font-medium" : "text-muted-foreground"}>
                            {s.active ? "Active" : "Deactivated"}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">{s.user_count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <SsoChangeConfirmDialog
        open={ssoConfirmOpen}
        onOpenChange={setSsoConfirmOpen}
        changes={ssoPendingChanges}
        onConfirm={() => {
          setSsoConfirmOpen(false)
          void doSaveOrganization()
        }}
      />

      <Dialog open={pharmacySiteDialogOpen} onOpenChange={setPharmacySiteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add pharmacy site</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreatePharmacySite} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="pharmacyName">Name</Label>
              <Input
                id="pharmacyName"
                value={newPharmacySiteName}
                onChange={(e) => setNewPharmacySiteName(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pharmacySso">Google SSO domain (optional)</Label>
              <Input
                id="pharmacySso"
                value={newPharmacySiteSso}
                onChange={(e) => setNewPharmacySiteSso(e.target.value)}
                placeholder="pharmacy.example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pharmacyEntra">Microsoft Entra tenant ID (optional)</Label>
              <Input
                id="pharmacyEntra"
                value={newPharmacySiteEntra}
                onChange={(e) => setNewPharmacySiteEntra(e.target.value)}
              />
            </div>
            <DialogFooter>
              <Button
                type="submit"
                disabled={!newPharmacySiteName.trim() || createOrganization.isPending}
              >
                {createOrganization.isPending ? "Creating..." : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Separator />

      {/* Tabs for Users and Assignments */}
      <Tabs defaultValue="users" className="space-y-4">
        <TabsList>
          <TabsTrigger value="users">Users</TabsTrigger>
          <TabsTrigger value="assignments">Clinical Review Assignments</TabsTrigger>
          <TabsTrigger value="workflows">Workflow Assignments</TabsTrigger>
          <TabsTrigger value="supplemental">Supplemental</TabsTrigger>
          <TabsTrigger value="quizzes">Assessment Availability</TabsTrigger>
          {isSuperAdmin && (
            <TabsTrigger value="apiApplications">API Applications</TabsTrigger>
          )}
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="space-y-1.5">
                  <CardTitle>Users</CardTitle>
                  <CardDescription>
                    Manage users for this organization
                  </CardDescription>
                </div>
                <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
                  <DialogTrigger asChild>
                    <Button>
                      <Plus className="mr-2 h-4 w-4" />
                      Add User
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <form onSubmit={handleCreateUser}>
                      <DialogHeader>
                        <DialogTitle>Add User</DialogTitle>
                        <DialogDescription>
                          Create a new user. A welcome email will be sent with a link to set their password.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="grid gap-4 py-4">
                        <div className="grid gap-2">
                          <Label htmlFor="username">Username</Label>
                          <Input
                            id="username"
                            value={newUsername}
                            onChange={(e) => setNewUsername(e.target.value)}
                            placeholder="Enter username"
                            autoFocus
                          />
                        </div>
                        <div className="grid gap-2">
                          <Label htmlFor="email">Email</Label>
                          <Input
                            id="email"
                            type="email"
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                            placeholder="Enter email address"
                          />
                        </div>
                        {/* No full name field. The name on a certificate of
                            completion is the person's own to give: they are
                            prompted for it before their first assessment. A
                            name guessed here would go on an accreditation
                            record unverified. */}
                      </div>
                      <DialogFooter>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setUserDialogOpen(false)}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          disabled={
                            !newUsername.trim() ||
                            !newEmail.trim() ||
                            createUser.isPending
                          }
                        >
                          {createUser.isPending ? "Creating..." : "Create User"}
                        </Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Username</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Last Login</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground">
                          No users found
                        </TableCell>
                      </TableRow>
                    ) : (
                      users.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              {user.username}
                              {user.role === "manager" && (
                                <Badge variant="secondary">Manager</Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>
                            {user.locked ? (
                              <span className="text-xs text-destructive font-medium">Locked</span>
                            ) : (
                              <span className={`text-xs ${user.active ? (user.sign_in_count === 0 ? "text-muted-foreground" : "text-lime-600 font-medium") : "text-muted-foreground"}`}>
                                {user.active ? (user.sign_in_count === 0 ? "Invited" : "Active") : "Deactivated"}
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {formatDate(user.last_sign_in_at)}
                          </TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onClick={() =>
                                    handleToggleUserActive(user.id, user.active)
                                  }
                                >
                                  {user.active ? "Deactivate" : "Activate"}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => handleToggleManager(user.id, user.role)}
                                >
                                  {user.role === "manager" ? "Remove manager" : "Make manager"}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => handleSendPasswordReset(user.id)}
                                >
                                  Send Password Reset
                                </DropdownMenuItem>
                                {user.locked && (
                                  <DropdownMenuItem
                                    onClick={() => handleUnlockUser(user.id)}
                                  >
                                    Unlock Account
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem
                                  onClick={() =>
                                    handleDeleteUser(user.id, user.username)
                                  }
                                  className="text-destructive"
                                >
                                  Delete User
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Clinical Review Assignments Tab */}
        <TabsContent value="assignments" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Clinical Review Assignments</CardTitle>
              <CardDescription>
                Control which clinical reviews are available to this organization
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {!assignmentsByType["clinical_review"] ? (
                <p className="text-sm text-muted-foreground">No clinical reviews available</p>
              ) : (
                <div className="space-y-2">
                  {assignmentsByType["clinical_review"].map((assignment) => {
                    const isActive =
                      assignmentChanges[assignment.document_id] ?? assignment.active
                    return (
                      <div
                        key={assignment.id}
                        className="flex items-center justify-between rounded-lg border p-3"
                      >
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="text-sm">
                            {assignment.document_title}
                          </span>
                          {assignment.compliance_tags?.map((tag) => (
                            <span key={tag} className="text-[10px] text-muted-foreground">
                              {tag}
                            </span>
                          ))}
                        </div>
                        <Switch
                          checked={isActive}
                          onCheckedChange={() =>
                            handleToggleAssignment(
                              assignment.document_id,
                              isActive
                            )
                          }
                        />
                      </div>
                    )
                  })}
                </div>
              )}
              {assignmentsByType["clinical_review"] && (
                <Button
                  onClick={handleSaveAssignments}
                  disabled={
                    Object.keys(assignmentChanges).length === 0 ||
                    updateAssignments.isPending
                  }
                >
                  {updateAssignments.isPending
                    ? "Saving..."
                    : "Save Assignments"}
                </Button>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Workflow Assignments Tab */}
        <TabsContent value="workflows" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Workflow Assignments</CardTitle>
              <CardDescription>
                Configure workflow and clinical review pairings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {Object.keys(workflowAssignmentsByWorkflow).length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No workflows or clinical reviews available
                </p>
              ) : (
                <>
                  {Object.entries(workflowAssignmentsByWorkflow).map(
                    ([workflowTitle, pairs]) => (
                      <div key={workflowTitle} className="space-y-3">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <h3 className="text-sm font-medium">{workflowTitle}</h3>
                          {pairs[0]?.workflow_compliance_tags?.map((tag) => (
                            <span key={tag} className="text-[10px] text-muted-foreground">
                              {tag}
                            </span>
                          ))}
                        </div>
                        <div className="space-y-2">
                          {pairs.map((wa) => {
                            const key = waKey(
                              wa.workflow_document_id,
                              wa.clinical_review_document_id
                            )
                            const isActive =
                              workflowAssignmentChanges[key] ?? wa.active
                            return (
                              <div
                                key={key}
                                className="flex items-center justify-between rounded-lg border p-3"
                              >
                                <div className="flex flex-wrap items-center gap-1.5">
                                  <span className="text-sm">
                                    {wa.clinical_review_document_title}
                                  </span>
                                  {wa.clinical_review_compliance_tags?.map((tag) => (
                                    <span key={tag} className="text-[10px] text-muted-foreground">
                                      {tag}
                                    </span>
                                  ))}
                                </div>
                                <Switch
                                  checked={isActive}
                                  onCheckedChange={() =>
                                    handleToggleWorkflowAssignment(
                                      wa.workflow_document_id,
                                      wa.clinical_review_document_id,
                                      isActive
                                    )
                                  }
                                />
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )
                  )}
                  <Button
                    onClick={handleSaveWorkflowAssignments}
                    disabled={
                      Object.keys(workflowAssignmentChanges).length === 0 ||
                      updateWorkflowAssignments.isPending
                    }
                  >
                    {updateWorkflowAssignments.isPending
                      ? "Saving..."
                      : "Save Workflow Assignments"}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Supplemental Assignments Tab */}
        <TabsContent value="supplemental" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Supplemental Assignments</CardTitle>
              <CardDescription>
                Control which supplemental documents are available to this organization
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {!assignmentsByType["supplemental"] ? (
                <p className="text-sm text-muted-foreground">No supplemental documents available</p>
              ) : (
                <div className="space-y-2">
                  {assignmentsByType["supplemental"].map((assignment) => {
                    const isActive =
                      assignmentChanges[assignment.document_id] ?? assignment.active
                    return (
                      <div
                        key={assignment.id}
                        className="flex items-center justify-between rounded-lg border p-3"
                      >
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="text-sm">
                            {assignment.document_title}
                          </span>
                          {assignment.compliance_tags?.map((tag) => (
                            <span key={tag} className="text-[10px] text-muted-foreground">
                              {tag}
                            </span>
                          ))}
                        </div>
                        <Switch
                          checked={isActive}
                          onCheckedChange={() =>
                            handleToggleAssignment(
                              assignment.document_id,
                              isActive
                            )
                          }
                        />
                      </div>
                    )
                  })}
                </div>
              )}
              {assignmentsByType["supplemental"] && (
                <Button
                  onClick={handleSaveAssignments}
                  disabled={
                    Object.keys(assignmentChanges).length === 0 ||
                    updateAssignments.isPending
                  }
                >
                  {updateAssignments.isPending
                    ? "Saving..."
                    : "Save Assignments"}
                </Button>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Quiz Availability Tab */}
        <TabsContent value="quizzes" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Assessment Availability</CardTitle>
              <CardDescription>
                Control which users in this organization each assessment is available to
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {quizAvailability.length === 0 ? (
                <p className="text-sm text-muted-foreground">No published assessments available</p>
              ) : (
                <div className="space-y-2">
                  {quizAvailability.map((row) => (
                    <div
                      key={row.quiz_id}
                      className="flex items-center justify-between rounded-lg border p-3"
                    >
                      <span className="text-sm">{row.title}</span>
                      <Switch
                        checked={quizIsActive(row)}
                        disabled={!canManageQuizzes}
                        onCheckedChange={() => handleToggleQuizAvailability(row)}
                        aria-label={`Assessment access to ${row.title}`}
                      />
                    </div>
                  ))}
                </div>
              )}
              {quizAvailability.length > 0 && canManageQuizzes && (
                <Button
                  onClick={handleSaveQuizAvailability}
                  disabled={
                    Object.keys(quizAvailabilityChanges).length === 0 ||
                    updateQuizAvailability.isPending
                  }
                >
                  {updateQuizAvailability.isPending
                    ? "Saving..."
                    : "Save Assessment Availability"}
                </Button>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* API Applications Tab (super_admin only) */}
        {isSuperAdmin && (
          <TabsContent value="apiApplications" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="space-y-1.5">
                    <CardTitle>API Applications</CardTitle>
                    <CardDescription>
                      Manage OAuth applications this organization uses to access the API
                    </CardDescription>
                  </div>
                  <Button onClick={openCreateApiApplication}>
                    <Plus className="mr-2 h-4 w-4" />
                    New Application
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Client ID</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Last used</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {apiApplicationsLoading ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-muted-foreground">
                            Loading...
                          </TableCell>
                        </TableRow>
                      ) : apiApplicationsError ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-muted-foreground">
                            You don&apos;t have access to this organization&apos;s API applications.
                          </TableCell>
                        </TableRow>
                      ) : apiApplications.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-muted-foreground">
                            No applications yet.
                          </TableCell>
                        </TableRow>
                      ) : (
                        apiApplications.map((app) => (
                          <TableRow key={app.id}>
                            <TableCell className="font-medium">{app.name}</TableCell>
                            <TableCell className="font-mono text-xs">{app.client_id}</TableCell>
                            <TableCell>
                              <span
                                className={`text-xs ${
                                  app.active ? "text-lime-600 font-medium" : "text-muted-foreground"
                                }`}
                              >
                                {app.active ? "Active" : "Revoked"}
                              </span>
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {app.last_used_at ? new Date(app.last_used_at).toLocaleString() : "Never"}
                            </TableCell>
                            <TableCell className="text-right space-x-2">
                              {app.active && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleRotateApiApplication(app.id)}
                                  >
                                    Rotate
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    onClick={() => handleRevokeApiApplication(app.id)}
                                  >
                                    Revoke
                                  </Button>
                                </>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            <Dialog
              open={apiAppDialogOpen}
              onOpenChange={(open) => (open ? setApiAppDialogOpen(true) : closeApiApplicationDialog())}
            >
              <DialogContent>
                {revealedApiApp ? (
                  <>
                    <DialogHeader>
                      <DialogTitle>Copy your client secret</DialogTitle>
                      <DialogDescription>
                        This secret is shown only once. Store it somewhere safe now.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2">
                      <Label>Client ID</Label>
                      <Input readOnly value={revealedApiApp.client_id} />
                      <Label>Client secret</Label>
                      <div className="flex gap-2">
                        <Input readOnly value={revealedApiApp.client_secret} />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            navigator.clipboard.writeText(revealedApiApp.client_secret)
                            toast.success("Copied")
                          }}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button onClick={closeApiApplicationDialog}>Done</Button>
                    </DialogFooter>
                  </>
                ) : (
                  <form onSubmit={handleCreateApiApplication}>
                    <DialogHeader>
                      <DialogTitle>New application</DialogTitle>
                      <DialogDescription>
                        Create OAuth credentials this organization can use to access the API.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-2 py-4">
                      <Label htmlFor="apiAppName">Name</Label>
                      <Input
                        id="apiAppName"
                        value={newApiAppName}
                        onChange={(e) => setNewApiAppName(e.target.value)}
                        placeholder="Prod integration"
                        autoFocus
                        required
                      />
                    </div>
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={closeApiApplicationDialog}>
                        Cancel
                      </Button>
                      <Button type="submit" disabled={!newApiAppName.trim() || createApiApplication.isPending}>
                        {createApiApplication.isPending ? "Creating..." : "Create"}
                      </Button>
                    </DialogFooter>
                  </form>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>
        )}
      </Tabs>
    </div>
  )
}
